// Authentication utility functions

export const logout = () => {
  // Clear JWT token from localStorage
  localStorage.removeItem('jwt');
  
  // Clear any other auth-related data
  localStorage.removeItem('user');
  localStorage.removeItem('authToken');
  
  // Clear session storage if any auth data is stored there
  sessionStorage.clear();
  
  // Redirect to login page
  window.location.href = '/login';
};

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('jwt');
  if (!token) return false;
  
  // Check if token is expired
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      // Token is expired, logout user
      logout();
      return false;
    }
    
    return true;
  } catch (error) {
    // Invalid token format, logout user
    logout();
    return false;
  }
};

export const getAuthToken = (): string | null => {
  const token = localStorage.getItem('jwt');
  if (!token) return null;
  
  // Check if token is expired
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      // Token is expired, logout user
      logout();
      return null;
    }
    
    return token;
  } catch (error) {
    // Invalid token format, logout user
    logout();
    return null;
  }
};

export const setAuthToken = (token: string) => {
  localStorage.setItem('jwt', token);
};

export const getUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

export const setUser = (user: any) => {
  localStorage.setItem('user', JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem('jwt');
  localStorage.removeItem('user');
  localStorage.removeItem('authToken');
  sessionStorage.clear();
};

// Check if JWT token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    if (!payload.exp) {
      return false; // No expiration means not expired
    }
    return payload.exp < currentTime;
  } catch (error) {
    return true; // Invalid token is considered expired
  }
};

// Get token expiration time
export const getTokenExpiration = (token: string): Date | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? new Date(payload.exp * 1000) : null;
  } catch (error) {
    return null;
  }
};

// Check if JWT token is expiring soon (within threshold seconds)
export const isTokenExpiringSoon = (token: string, thresholdSeconds: number = 300): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp && (payload.exp - currentTime) < thresholdSeconds && (payload.exp - currentTime) > 0;
  } catch (error) {
    return false;
  }
}; 